import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import {
  ArrowRight, Gift, UserPlus, Star, Quote, Target, Eye, Heart, Award, User, Phone, Globe, Calendar, Briefcase,
  FileCheck2, PhoneCall, ShieldCheck, Sparkles, ArrowDown, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  NAV_CATEGORIES, EXPERIENCE_STATS, WHY_CHOOSE, REVIEWS, getServiceGuide,
  LANGUAGES, SOURCES_OF_INCOME,
  defaultSpokenLanguageForUiLocale,
  SERVICE_CATALOG,
} from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { serviceTitle, serviceDesc, serviceStartingPrice } from '../i18n/serviceCopy';
import { getJourneyId, trackEvent, trackPageLifecycle } from '../lib/customerEventTracker';
import { SOURCE_ITR_PRICING_GUIDE } from '../data/itrPricingGuide';
import { hydrateItrPricingGuideRows } from '../lib/pricingConfigClient';

const categoryHref = (cat, code) =>
  cat === 'INCOME_TAX' ? `/file-itr?service=${code}` : `/get-started?service=${code}`;

export const AllServicesGrid = () => {
  const { t } = useLanguage();
  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);
  const catDesc = (cat) => t(`category.${cat.category}_desc`, cat.desc);
  return (
    <section id="services" className="py-20 bg-gradient-to-b from-white to-teal-50/30">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('allsvc.title_all', 'All')} <span className="text-[#14A098]">{t('allsvc.title_services', 'Services')}</span>
          </h2>
          <p className="text-gray-600 mt-8 max-w-2xl mx-auto">{t('allsvc.subtitle', 'Click any service below to apply — our experts will reach out within 2 hours.')}</p>
        </div>
        <div className="space-y-10">
          {NAV_CATEGORIES.map((cat) => (
            <div key={cat.category}>
              <div className="flex flex-wrap items-end justify-between mb-5">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#14A098]">{catLabel(cat)}</div>
                  <div className="text-sm text-gray-500">{catDesc(cat)}</div>
                </div>
                <div className="text-xs text-gray-500">{t('allsvc.services_count', '{n} services', { n: cat.services.length })}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cat.services.map((svc) => (
                  <Link key={svc.code} to={categoryHref(cat.category, svc.code)} className="card-lift group p-5 rounded-2xl border border-gray-100 bg-white">
                    <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center mb-3 group-hover:bg-gradient-to-br group-hover:from-[#14A098] group-hover:to-[#5eead4] transition-colors">
                      <svc.icon className="w-5 h-5 text-[#14A098] group-hover:text-white" />
                    </div>
                    <h3 className="font-bold text-[#0f2540] mb-1 leading-snug">{serviceTitle(svc.code, t, svc.title)}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{serviceDesc(svc.code, t, svc.desc)}</p>
                    <div className="mt-2 inline-flex items-center rounded-full border border-[#14A098]/25 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-[#0f2540]">
                      {serviceStartingPrice(svc.code, t, getServiceGuide(svc.code)?.startingPrice)}
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#14A098]">
                      {t('allsvc.fill_form', 'Fill form now')} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ABOUT_CARDS = [
  { id: 'mission', icon: Target, title: 'Our Mission', desc: 'Deliver personalised tax solutions that ensure compliance while optimising financial outcomes.' },
  { id: 'vision', icon: Eye, title: 'Our Vision', desc: "Become India's most trusted tax advisory firm known for expertise and integrity." },
  { id: 'values', icon: Heart, title: 'Our Values', desc: 'Integrity, transparency, client-first mindset and continuous learning.' },
  { id: 'promise', icon: Award, title: 'Our Promise', desc: 'Accurate filing, timely delivery and honest advice — every single time.' },
];

const EXP_LABEL_KEYS = ['exp.years', 'exp.clients', 'exp.returns', 'exp.regs'];

export const AboutSection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('about.title_about', 'About')} <span className="text-[#14A098]">{t('about.title_brand', 'SolveTax')}</span>
          </h2>
          <p className="text-gray-600 mt-8">{t('about.subtitle', 'Your trusted partner for all tax and financial compliance needs')}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-5 text-gray-700 leading-relaxed">
            <h3 className="text-2xl md:text-3xl font-bold text-[#0f2540] mb-2">{t('about.story_title', 'Our Story')}</h3>
            <p>{t('about.p1', 'Founded with a vision to make taxation simple and accessible, SolveTax has grown into a trusted name in the Indian tax consultancy landscape. We started as a small practice helping individuals file their returns and today serve over 5000 satisfied clients across the country.')}</p>
            <p>{t('about.p2', 'Our team of qualified tax agents, tax experts, and financial advisors bring decades of combined experience to every engagement. We stay ahead of changing tax laws so you do not have to.')}</p>
            <p>{t('about.p3', 'Whether you are a salaried professional filing your ITR, a startup navigating GST complexities, or an established business managing audits — we bring clarity, accuracy, and peace of mind.')}</p>
            <div className="flex flex-wrap gap-3 pt-3">
              <Link to="/about"><Button className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full">{t('about.read_full', 'Read Full Story')} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/file-itr"><Button variant="outline" className="border-[#14A098] text-[#14A098] rounded-full hover:bg-[#14A098] hover:text-white">{t('about.file_itr_now', 'File ITR Now')}</Button></Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {ABOUT_CARDS.map((c) => (
              <div key={c.id} className="card-lift p-6 rounded-2xl bg-teal-50/50 border border-teal-100">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm mb-3"><c.icon className="w-6 h-6 text-[#14A098]" /></div>
                <h4 className="font-bold text-[#0f2540] mb-2">{t(`about.${c.id}_title`, c.title)}</h4>
                <p className="text-sm text-gray-600">{t(`about.${c.id}_desc`, c.desc)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 rounded-3xl bg-gradient-to-r from-[#0f2540] to-[#14A098] p-10 md:p-14 shadow-xl">
          <h3 className="text-white text-2xl font-bold mb-8">{t('about.experience_title', 'Our Experience')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {EXPERIENCE_STATS.map((s, i) => (
              <div key={s.label} className="text-white">
                <div className="text-4xl md:text-5xl font-extrabold mb-2">{s.value}</div>
                <div className="text-sm text-teal-100">{t(EXP_LABEL_KEYS[i] || 'exp.years', s.label)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const ReferralSection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-teal-50/40">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4">
            <Gift className="w-4 h-4 text-[#14A098]" />
            <span className="text-xs font-semibold text-[#0f2540]">{t('referral.badge', 'Refer & Earn')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-4">{t('referral.title', 'Our Referral Program')}</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{t('referral.p1', 'At SolveTax, we believe in building relationships through trust and quality service. Our referral program rewards you for sharing your positive experience with others.')}</p>
          <p className="text-gray-600 mb-8 leading-relaxed">{t('referral.p2', 'When you refer a friend or business associate to SolveTax, both you and your referral receive special benefits and discounts on our services.')}</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/referral"><Button className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full">{t('referral.know_more', 'Know More')} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/referral#referral-form"><Button variant="outline" className="border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white rounded-full">{t('referral.join', 'Join Referral Program')}</Button></Link>
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-[#14A098]/10 to-transparent rounded-[2rem] blur-xl" />
          <div className="relative bg-white rounded-3xl p-10 shadow-xl border border-gray-100 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-lg mb-6">
              <UserPlus className="w-10 h-10 text-white" />
            </div>
            <div className="text-5xl font-extrabold text-[#0f2540]">₹200</div>
            <div className="text-sm uppercase tracking-wider text-[#14A098] font-semibold mt-2">{t('referral.reward_label', 'Reward per referral')}</div>
            <div className="mt-6 text-sm text-gray-500">{t('referral.reward_note', 'Earn cashback every time your referral files a return with us.')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const WhyChoose = () => {
  const { t, locale } = useLanguage();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {locale === 'te' ? (
              <span className="text-[#14A098]">{t('why.title_brand', 'Choose SolveTax')}</span>
            ) : (
              <>
                {t('why.title_why', 'Why')}{' '}
                <span className="text-[#14A098]">{t('why.title_brand', 'Choose SolveTax')}</span>
              </>
            )}
          </h2>
          <p className="text-gray-600 mt-8">{t('why.subtitle', 'What sets us apart from other tax service providers')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_CHOOSE.map((w, i) => (
            <div key={w.title} className="card-lift p-8 rounded-2xl bg-white border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mb-4"><w.icon className="w-7 h-7 text-[#14A098]" /></div>
              <h3 className="font-bold text-[#0f2540] mb-2 text-lg">{t(`why.c${i + 1}_title`, w.title)}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{t(`why.c${i + 1}_desc`, w.desc)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const { t } = useLanguage();
  // Split 10 reviews into 2 rows of 5; each row duplicated for seamless loop
  const row1 = REVIEWS.slice(0, 5);
  const row2 = REVIEWS.slice(5, 10);
  return (
    <section className="py-20 bg-gradient-to-b from-teal-50/40 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('reviews.title_lead', 'What Our')}{' '}
            <span className="text-[#14A098]">{t('reviews.title_accent', 'Clients Say')}</span>
          </h2>
          <p className="text-gray-600 mt-8">{t('reviews.subtitle', 'Reviews from real Indian taxpayers across the country')}</p>
        </div>

        {/* Row 1 — scrolls right to left */}
        <div className="marquee-mask mb-6">
          <div className="marquee marquee-left">
            {[...row1, ...row1].map((r, idx) => (
              <ReviewCard key={`r1-${r.id}-${idx}`} r={r} />
            ))}
          </div>
        </div>

        {/* Row 2 — scrolls left to right */}
        <div className="marquee-mask">
          <div className="marquee marquee-right">
            {[...row2, ...row2].map((r, idx) => (
              <ReviewCard key={`r2-${r.id}-${idx}`} r={r} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ReviewCard = ({ r }) => (
  <article className="shrink-0 w-[340px] md:w-[380px] p-7 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow relative">
    <Quote className="w-8 h-8 text-[#14A098]/15 absolute top-5 right-5" />
    <div className="flex items-center gap-1 text-amber-400 mb-4">
      {Array.from({ length: r.rating }, (_, i) => i + 1).map((n) => (
        <Star key={`${r.id}-s${n}`} className="w-4 h-4 fill-current" />
      ))}
    </div>
    <p className="text-sm text-gray-700 leading-relaxed mb-5 line-clamp-5" lang={r.langCode}>“{r.quote}”</p>
    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center text-white font-bold shrink-0">{r.initials}</div>
      <div className="min-w-0">
        <div className="font-semibold text-[#0f2540] text-sm truncate">{r.name}</div>
        <div className="text-xs text-gray-500 truncate">{r.city} • {r.service}</div>
      </div>
    </div>
  </article>
);

export const CTABand = () => {
  const { t } = useLanguage();
  return (
    <section className="py-16 bg-[#0f2540] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#14A098]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5eead4]/10 rounded-full blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8 flex flex-col md:flex-row gap-6 items-center justify-between">
        <div>
          <h3 className="text-white text-2xl md:text-3xl font-extrabold mb-2">{t('cta.title', 'Ready to simplify your taxes?')}</h3>
          <p className="text-teal-100/80">{t('cta.subtitle', 'Get started today and file with confidence in minutes.')}</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center md:justify-end">
          <Link to="/file-itr">
            <Button
              size="lg"
              onClick={() => trackEvent({
                event_name: 'cta_click',
                event_action: 'click',
                event_status: 'success',
                event_category: 'navigation',
                cta_name: 'file_itr',
                page_path: '/home',
              })}
              className="bg-white text-[#0f2540] hover:bg-teal-50 rounded-full px-8 h-12 font-semibold shadow-lg inline-flex items-center gap-2"
            >
              {t('cta.file_itr', 'File ITR')} <ArrowRight className="w-4 h-4 shrink-0" />
            </Button>
          </Link>
          <Link to="/get-started">
            <Button
              size="lg"
              onClick={() => trackEvent({
                event_name: 'cta_click',
                event_action: 'click',
                event_status: 'success',
                event_category: 'navigation',
                cta_name: 'multi_service',
                page_path: '/home',
              })}
              className="bg-white text-[#0f2540] hover:bg-teal-50 rounded-full px-4 sm:px-8 min-h-12 h-auto py-3 font-semibold text-sm sm:text-base whitespace-normal text-center leading-snug shadow-lg inline-flex items-center justify-center gap-2 sm:gap-2.5"
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 shrink-0" aria-hidden />
              <span>{t('cta.multi_service', 'You can request multiple services')}</span>
              <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export const HomeSpecialOffer = () => {
  const { t } = useLanguage();
  return (
    <section className="py-8 bg-white">
      <div className="w-full max-w-[1680px] mx-auto px-2 md:px-4">
        <Card className="p-5 rounded-2xl bg-gradient-to-br from-[#0f2540] to-[#14A098] text-white border-0 shadow-lg">
          <div className="flex items-center gap-2 mb-2"><Gift className="w-5 h-5" /><span className="font-semibold">{t('offer.title', 'Special Offer')}</span></div>
          <p className="text-base md:text-lg text-teal-100 text-center">
            {t('offer.body', 'Submit the form and get a flat 10% discount on your first service with SolveTax.')}
          </p>
        </Card>
      </div>
    </section>
  );
};

const HOME_ITR_INITIAL = {
  client_name: '',
  mobile: '',
  language: '',
  financial_year: '2024-25',
  source_of_income: 'Multiple Sources',
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || process.env.VITE_API_BASE_URL || process.env.VITE_API_URL || '';
const PUBLIC_API_KEY = process.env.REACT_APP_PUBLIC_API_KEY || process.env.VITE_PUBLIC_API_KEY || '';

const HomeIncomeTaxMiniForm = () => {
  const { t, locale } = useLanguage();
  const homeItrPromoCta = useMemo(() => {
    const meta = SERVICE_CATALOG.find((s) => s.code === 'ITR_FILING');
    const name = serviceTitle('ITR_FILING', t, meta?.title || 'Income Tax Return Filing');
    return t('common.get_my_service_done', 'Get my {service} done', { service: name });
  }, [t]);
  const [form, setForm] = useState(() => ({
    ...HOME_ITR_INITIAL,
    language: defaultSpokenLanguageForUiLocale(locale),
  }));
  const [sourcePriceGuide, setSourcePriceGuide] = useState(SOURCE_ITR_PRICING_GUIDE);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const next = await hydrateItrPricingGuideRows(SOURCE_ITR_PRICING_GUIDE);
        if (active && next?.length) setSourcePriceGuide(next);
      } catch {
        /* keep static fallback prices */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const def = defaultSpokenLanguageForUiLocale(locale);
    if (!def) return;
    setForm((prev) => (prev.language ? prev : { ...prev, language: def }));
  }, [locale]);

  const selectedSource = form.source_of_income || 'Multiple Sources';
  const selectedSourceGuide = useMemo(
    () => sourcePriceGuide.find((item) => item.source === selectedSource),
    [sourcePriceGuide, selectedSource],
  );

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'ITR_HOME_MINI_FORM',
      form_name: 'HomeIncomeTaxMiniForm',
      service_code: 'ITR_FILING',
      open_event_name: 'itr_home_form_open',
    }),
    [],
  );

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.client_name.trim()) next.client_name = 'Name is required';
    if (!/^[0-9]{10}$/.test(form.mobile)) next.mobile = 'Enter valid 10-digit mobile';
    if (!form.language) next.language = 'Select language';
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      trackEvent({
        event_name: 'itr_home_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'HomeIncomeTaxMiniForm',
        funnel_name: 'ITR_HOME_MINI_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_HOME_MINI_FORM'),
        service_code: 'ITR_FILING',
        error_code: 'validation_error',
        error_message: 'Client-side validation failed',
      });
      return;
    }
    trackEvent({
      event_name: 'itr_home_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'HomeIncomeTaxMiniForm',
      funnel_name: 'ITR_HOME_MINI_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('ITR_HOME_MINI_FORM'),
      service_code: 'ITR_FILING',
      phone_number: form.mobile,
    });
    const payload = {
      client_name: form.client_name.trim(),
      mobile: form.mobile,
      language: form.language,
      state: null,
      priority: 'NORMAL',
      remarks: null,
      pan_number: null,
      password: null,
      financial_year: form.financial_year,
      filed_status: 'NOT_FILED',
      referral_id: null,
      referral_entity: null,
      email_id: null,
      source_of_income: form.source_of_income,
      refund_amount: null,
      rm_id: null,
      op_id: null,
      tag: 'client_website',
    };

    setSubmitting(true);
    try {
      if (API_BASE) {
        await axios.post(`${API_BASE}/api/v1/income-tax`, payload, {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
          },
        });
        trackEvent({
          event_name: 'itr_home_submit_success',
          event_action: 'submit',
          event_status: 'success',
          event_category: 'form',
          form_name: 'HomeIncomeTaxMiniForm',
          funnel_name: 'ITR_HOME_MINI_FORM',
          funnel_step_number: 2,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('ITR_HOME_MINI_FORM', { reset: true }),
          service_code: 'ITR_FILING',
          phone_number: form.mobile,
          api_name: '/api/v1/income-tax',
          api_status_code: 201,
        });
        toast.success('Submitted successfully. Our team will contact you in 2 hours.');
      } else {
        toast.success('Form works in UI. Add API base URL to submit backend.');
      }
      setForm({ ...HOME_ITR_INITIAL, language: defaultSpokenLanguageForUiLocale(locale) });
      setErrors({});
    } catch (err) {
      const message = err?.response?.data?.detail?.error?.message || err?.response?.data?.detail || err?.message || 'Submission failed';
      trackEvent({
        event_name: 'itr_home_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'HomeIncomeTaxMiniForm',
        funnel_name: 'ITR_HOME_MINI_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_HOME_MINI_FORM'),
        service_code: 'ITR_FILING',
        phone_number: form.mobile,
        api_name: '/api/v1/income-tax',
        api_status_code: err?.response?.status || null,
        error_code: err?.response?.status ? `api_${err.response.status}` : 'api_error',
        error_message: String(message),
      });
      toast.error(`Could not submit: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card id="home-itr-form" className="w-full max-w-none sm:max-w-[560px] ml-0 sm:ml-auto p-5 md:p-6 rounded-2xl border border-gray-100 shadow-xl bg-white scroll-mt-24">
      <form className="space-y-5" onSubmit={onSubmit}>
        <div>
          <div>
            <h3 className="text-2xl font-bold text-[#0f2540]">{t('itr.form_personal', 'Personal Details')}</h3>
          </div>
          <div className="mt-3 border-t border-gray-100 pt-3 space-y-3">
            <div>
              <Label className="text-sm font-semibold text-[#0f2540] flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-[#14A098]" />{t('itr.full_name', 'Full Name')} <span className="text-rose-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder={t('itr.placeholder_name', 'e.g. Rahul Sharma')} value={form.client_name} onChange={(e) => setField('client_name', e.target.value)} />
              {errors.client_name && <p className="text-xs text-rose-500 mt-1">{errors.client_name}</p>}
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#0f2540] flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#14A098]" />{t('itr.mobile', 'Mobile Number')} <span className="text-rose-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder={t('itr.placeholder_mobile', '10-digit mobile')} value={form.mobile} onChange={(e) => setField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} inputMode="numeric" />
              {errors.mobile && <p className="text-xs text-rose-500 mt-1">{errors.mobile}</p>}
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#0f2540] flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#14A098]" />{t('itr.pref_lang', 'Preferred Language')} <span className="text-rose-500">*</span></Label>
              <Select value={form.language} onValueChange={(v) => setField('language', v)}>
                <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder={t('itr.placeholder_lang', 'Select language')} /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent>
              </Select>
              {errors.language && <p className="text-xs text-rose-500 mt-1">{errors.language}</p>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-0.5">
          <Button type="submit" className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-7 h-11 whitespace-normal text-center leading-snug" disabled={submitting}>
            {submitting ? t('itr.submitting', 'Submitting...') : <>{t('form.submit_details', 'Submit Details')} <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full h-11 border-gray-300"
            onClick={() => {
              setForm({ ...HOME_ITR_INITIAL, language: defaultSpokenLanguageForUiLocale(locale) });
              setErrors({});
            }}
          >
            {t('itr.reset', 'Reset')}
          </Button>
        </div>
        <div className="space-y-3 pt-1">
          <h4 className="text-2xl font-extrabold text-[#0f2540]">{t('itf.filing_opts', 'ITR Filing options')}</h4>
          <div className="flex flex-wrap gap-2">
            {SOURCES_OF_INCOME.map((source) => {
              const active = selectedSource === source;
              return (
                <button
                  key={source}
                  type="button"
                  onClick={() => setField('source_of_income', source)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                    active
                      ? 'bg-[#14A098] border-[#14A098] text-white'
                      : 'bg-white border-gray-200 text-[#0f2540] hover:border-[#14A098]/40'
                  }`}
                >
                  {source}
                </button>
              );
            })}
          </div>
        </div>
        <div className="rounded-xl border border-[#14A098]/25 bg-teal-50/40 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#14A098]">{t('sf.starting_price', 'Starting price')}</p>
          <p className="text-2xl font-extrabold text-[#0f2540] mt-1 tabular-nums">{selectedSourceGuide?.price || t('services.price_on_request', 'Price on request')}</p>
        </div>
      </form>
    </Card>
  );
};

export const IncomeTaxHighlightSection = () => {
  const { t } = useLanguage();
  const [showMobileProcess, setShowMobileProcess] = useState(false);
  const homeItrPromoCta = useMemo(() => {
    const meta = SERVICE_CATALOG.find((s) => s.code === 'ITR_FILING');
    const name = serviceTitle('ITR_FILING', t, meta?.title || 'Income Tax Return Filing');
    return t('common.get_my_service_done', 'Get my {service} done', { service: name });
  }, [t]);
  const quickSteps = [
    {
      id: 'step1',
      label: t('itr.step1', 'Step 1'),
      icon: FileCheck2,
      text: t('itr.s1_text', 'When your details are complete, tap "{cta}"', {
        cta: t('form.submit_details', 'Submit Details'),
      }),
    },
    { id: 'step2', label: t('itr.step2', 'Step 2'), icon: PhoneCall, text: t('itr.s2_text', 'ITR expert callback from our team within 2 hours') },
    { id: 'step3', label: t('itr.step3', 'Step 3'), icon: Briefcase, text: t('itr.s3_docs_text', 'Our team collects the documents needed for your ITR') },
    { id: 'step4', label: t('itr.step4', 'Step 4'), icon: ShieldCheck, text: t('itr.s3_text', 'We will provide the ITR services') },
    { id: 'step5', label: t('itr.step5', 'Step 5'), icon: Sparkles, text: t('itr.s4_text', 'Get your ITR filed quickly') },
  ];
  return (
    <section className="py-8 hero-gradient">
      <div className="w-full max-w-[1680px] mx-auto px-2 sm:px-3 md:px-5 lg:px-6">
        {/* App view: show this header first (above Benefits) */}
        <div className="lg:hidden text-center mb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4 shadow-sm">
            <span className="text-xs font-semibold text-[#0f2540]">{t('itr.badge_line', 'Income Tax Return Filing • AY 2025-26')}</span>
          </div>
          <div className="mb-3 flex items-center justify-center gap-2">
            <h2 className="ui-title-lg mb-0">{t('itr.main_title', 'Income Tax Return Filing')}</h2>
            <Button
              type="button"
              onClick={() => setShowMobileProcess(true)}
              className="rounded-full h-9 px-4 bg-[#14A098] border-2 border-[#14A098] text-white hover:bg-[#0e7a74] hover:border-[#0e7a74]"
            >
              {t('itr.process_btn', 'Process')}
            </Button>
          </div>
          <p className="ui-body max-w-3xl mx-auto mb-4">
            {t('itr.main_sub', 'Fill this simple form in 2 minutes. Our tax expert will call you within 2 hours and help you file your ITR quickly and correctly.')}
          </p>
          <a
            href="#home-itr-form"
            onClick={() =>
              trackEvent({
                event_name: 'cta_click',
                event_action: 'click',
                event_status: 'success',
                event_category: 'navigation',
                cta_name: 'home_mobile_fill_form_get_itr',
                page_path: '/home',
              })
            }
            className="mt-4 inline-flex w-full max-w-[360px] items-center justify-center gap-2 rounded-full border-2 border-[#14A098] bg-[#14A098] px-5 h-11 text-sm font-semibold text-white shadow-md shadow-[#14A098]/35 transition-colors hover:bg-[#0e7a74] hover:border-[#0e7a74] active:bg-[#0c6b66] active:border-[#0c6b66] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14A098] focus-visible:ring-offset-2 whitespace-normal text-center leading-snug"
          >
            {homeItrPromoCta}
            <ArrowRight className="h-4 w-4 shrink-0 opacity-95" aria-hidden />
          </a>
        </div>
        {showMobileProcess && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/45 p-3 overflow-y-auto">
            <div className="mx-auto mt-6 w-full max-w-xl">
              <Card className="p-4 rounded-2xl border-2 border-[#14A098]/40 bg-white shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#14A098]/10 border border-[#14A098]/25">
                    <span className="w-2 h-2 rounded-full bg-[#14A098]" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#0f2540]">{t('itr.quick_process', 'Quick Process')}</span>
                  </div>
                  <button type="button" onClick={() => setShowMobileProcess(false)} className="w-8 h-8 rounded-full border border-gray-200 inline-flex items-center justify-center text-[#0f2540]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="text-2xl font-extrabold text-[#0f2540] mb-4">{t('itr.how_title', 'How it works')}</h3>
                <div className="space-y-3">
                  {quickSteps.map((item, idx) => (
                    <div key={item.id} className={`rounded-xl border shadow-md p-3 text-white ${idx % 2 === 0 ? 'bg-gradient-to-r from-[#0f7f79] to-[#14A098] border-[#14A098]' : 'bg-gradient-to-r from-cyan-500 to-teal-500 border-cyan-500'}`}>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 mb-1">{item.label}</div>
                      <div className="flex items-start gap-2 justify-between">
                        <div className="flex items-start gap-2 min-w-0">
                          <item.icon className="w-4 h-4 text-white shrink-0 mt-0.5" />
                          <span className="font-semibold leading-snug">{item.text}</span>
                        </div>
                        <ArrowDown className="w-4 h-4 text-white shrink-0 opacity-90" strokeWidth={2.5} />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-5 xl:gap-6 items-start">
          <div className="lg:col-span-5 xl:col-span-5 space-y-4 w-full min-w-0 max-w-[620px] mx-auto lg:ml-auto lg:mr-0">
            <Card className="rotating-border-soft p-5 rounded-2xl shadow-md shadow-[#14A098]/15">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{t('itr.benefits_label', 'Benefits')}</p>
              <p className="text-2xl leading-tight font-extrabold text-[#0f2540] mt-2">
                {t('itr.benefits_line', 'Get Maximum Refunds and Guidance on Deductions')}
              </p>
              <div className="mt-4 pt-4 border-t border-emerald-100">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#14A098] flex items-center justify-center shadow-[0_4px_14px_rgba(20,160,152,0.35)]"
                    aria-hidden
                  >
                    <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                      <span className="text-2xl sm:text-[1.65rem] font-extrabold text-[#0f2540] tracking-tight tabular-nums">
                        ₹200
                      </span>
                      <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-[#14A098]">
                        {t('referral.reward_label', 'Reward per referral')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-snug">
                      {t(
                        'itr.benefit_referral_line',
                        'Get ₹200 on every successful ITR referral when they file their return with us.',
                      )}
                    </p>
                  </div>
                </div>
                <Link
                  to="/referral#referral-form"
                  onClick={() =>
                    trackEvent({
                      event_name: 'cta_click',
                      event_action: 'click',
                      event_status: 'success',
                      event_category: 'navigation',
                      cta_name: 'benefits_submit_referral',
                      page_path: '/home',
                    })
                  }
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#14A098] bg-white px-4 py-2.5 text-sm font-semibold text-[#14A098] shadow-sm transition-colors hover:bg-[#14A098] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14A098]/35 focus-visible:ring-offset-2"
                >
                  <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                  {t('nav.submit_referral', 'Submit Referral')}
                </Link>
              </div>
            </Card>
            <Card id="home-quick-process" className="hidden lg:block p-5 rounded-2xl border-2 border-[#14A098]/40 bg-white shadow-md shadow-[#14A098]/15">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#14A098]/10 border border-[#14A098]/25 mb-4">
                <span className="w-2 h-2 rounded-full bg-[#14A098]" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[#0f2540]">{t('itr.quick_process', 'Quick Process')}</span>
              </div>
              <h3 className="text-3xl font-extrabold text-[#0f2540] mb-4">{t('itr.how_title', 'How it works')}</h3>
              <div className="space-y-3 text-sm">
                {quickSteps.map((item, idx) => (
                  <div key={item.id} className={`rounded-xl border shadow-md p-3 text-white ${idx % 2 === 0 ? 'bg-gradient-to-r from-[#0f7f79] to-[#14A098] border-[#14A098]' : 'bg-gradient-to-r from-cyan-500 to-teal-500 border-cyan-500'}`}>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 mb-1">{item.label}</div>
                    <div className="flex items-start gap-2 justify-between">
                      <div className="flex items-start gap-2 min-w-0">
                        <item.icon className="w-4 h-4 text-white shrink-0 mt-0.5" />
                        <span className="font-semibold leading-snug">{item.text}</span>
                      </div>
                      <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5 text-white shrink-0 opacity-90" strokeWidth={2.5} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-7 xl:col-span-7 w-full min-w-0 max-w-[860px] mr-auto lg:-translate-x-20 xl:-translate-x-28">
            {/* Desktop header stays here; hidden in app view */}
            <div className="hidden lg:block text-center mb-4 lg:ml-16 xl:ml-24">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4 shadow-sm">
                <span className="text-xs font-semibold text-[#0f2540]">{t('itr.badge_line', 'Income Tax Return Filing • AY 2025-26')}</span>
              </div>
              <div className="mb-3 flex items-center justify-center gap-2">
                <h2 className="ui-title-lg mb-0">{t('itr.main_title', 'Income Tax Return Filing')}</h2>
                <a href="#home-quick-process">
                  <Button type="button" className="rounded-full h-9 px-4 bg-[#14A098] border-2 border-[#14A098] text-white hover:bg-[#0e7a74] hover:border-[#0e7a74]">
                    {t('itr.process_btn', 'Process')}
                  </Button>
                </a>
              </div>
              <p className="ui-body max-w-3xl mx-auto mb-4">
                {t('itr.main_sub', 'Fill this simple form in 2 minutes. Our tax expert will call you within 2 hours and help you file your ITR quickly and correctly.')}
              </p>
            </div>
            <div className="lg:-translate-x-8 xl:-translate-x-12">
              <HomeIncomeTaxMiniForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
